import React, { useRef, useState, useEffect, useMemo } from "react";
import "../common/AddNewLicensePanel.css";
import Icon from "../../../common/Icon";
import { toast } from "react-toastify";
import PageLoader from "../../../shared/PageLoader";
import { getUserDetails } from "../../../../backend/auth";
import CostDetailsForm, { CostFormData } from "../common/CostDetailsForm";
import { downloadS3FileSecurely } from "../../../../utils/common";

const COST_TYPE_OPTIONS = [
  { id: 1, label: "Professional Service Fees", key: "professional_service_fees" },
  { id: 2, label: "Statutory / Government Fees", key: "statutory_government_fees" },
  { id: 3, label: "Subscription / License Fee", key: "subscription_license_fee" },
  { id: 4, label: "Documentation / Processing Charges", key: "documentation_processing_charges" },
  { id: 5, label: "Renewal Fees", key: "renewal_fees"},
  { id: 6, label: "Late Fees / Penalties", key: "late_fees_penalties" },
  { id: 7, label: "Miscellaneous", key: "miscellaneous" },
];
import {
  useCreateLicenseApplication,
  useGetLicenseTypes,
  useGetApplicationTypes,
  useGetLicenseApplications,
  useGetCompanyLicenses,
  FULL_USER_COMPANIES_PAYLOAD,
} from "../../../../backend/license";
import { useGetUserCompanies } from "../../../../backend/query";
import {
  useGetStates,
  useGetCities,
} from "../../../../backend/masters";
import { useGetUsers } from "../../../../backend/users";

interface SidePanelProps {
  isOpen: boolean;
  closePanel: () => void;
  onSuccess?: () => void;
}

const NewApplicationPanel: React.FC<SidePanelProps> = ({
  isOpen,
  closePanel,
  onSuccess,
}) => {
  const user = getUserDetails();
  const [currentStep, setCurrentStep] = useState(1);

  // Cost details popup state
  const [costFormPopup, setCostFormPopup] = useState<{
    isOpen: boolean;
    costTypeId: number | null;
    costTypeLabel: string;
    costTypeKey: string;
  }>({
    isOpen: false,
    costTypeId: null,
    costTypeLabel: "",
    costTypeKey: "",
  });

  // Track expanded cost detail accordions
  const [expandedCostAccordions, setExpandedCostAccordions] = useState<string[]>([]);

  // Confirmation modal for cost type deletion
  const [costTypeToDelete, setCostTypeToDelete] = useState<{
    id: number;
    label: string;
    key: string;
  } | null>(null);

  // Form state
  const [formData, setFormData] = useState<any>({
    company_id: "",
    associate_company_id: "",
    location_id: "",
    license_type_id: "",
    state_id: "",
    city_id: "",
    license_name: "",
    authority: "",
    application_type_id: "",
    assigned_to_id: "",
    requested_by_id: "",
    editor_ids: [],
    watcher_ids: [] as (string | number)[],
    cost_type_ids: [] as number[],
    cost_details: {} as Record<string, CostFormData>,
    status: "not_started",
    description: "",
  });

  const resetForm = () => {
    setFormData({
      company_id: "",
      associate_company_id: "",
      location_id: "",
      license_type_id: "",
      state_id: "",
      city_id: "",
      license_name: "",
      authority: "",
      application_type_id: "",
      assigned_to_id: "",
      requested_by_id: "",
      editor_ids: [],
      watcher_ids: [],
      cost_type_ids: [],
      cost_details: {},
      status: "not_started",
      description: "",
    });
    setCurrentStep(1);
    setCostFormPopup({
      isOpen: false,
      costTypeId: null,
      costTypeLabel: "",
      costTypeKey: "",
    });
    setExpandedCostAccordions([]);
    setAssociateCompanies([]);
    setLocations([]);
    setUploadedFiles([]);
  };

  // Handle close panel with reset
  const handleClosePanel = () => {
    resetForm();
    closePanel();
  };

  const { userCompanies, isFetching: loadingCompanies } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const [companies, setCompanies] = useState<any[]>([]);
  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);

  // Process companies
  useEffect(() => {
    if (userCompanies && userCompanies.length > 0) {
      setCompanies(userCompanies);
    }
  }, [userCompanies]);

  // Cascade: Company → Associate Companies
  useEffect(() => {
    if (formData.company_id && companies.length > 0) {
      const selectedCompany = companies.find(
        (c: any) => c.id === formData.company_id
      );
      if (selectedCompany && selectedCompany.associate_companies) {
        setAssociateCompanies(selectedCompany.associate_companies);
      } else {
        setAssociateCompanies([]);
      }
      // Reset dependent fields
      setFormData((prev: any) => ({
        ...prev,
        associate_company_id: "",
        location_id: "",
      }));
      setLocations([]);
    } else {
      setAssociateCompanies([]);
    }
  }, [formData.company_id, companies]);

  // Cascade: Associate Company → Locations
  useEffect(() => {
    if (formData.associate_company_id && associateCompanies.length > 0) {
      const selectedAssocCompany = associateCompanies.find(
        (ac: any) => ac.id === formData.associate_company_id
      );
      if (selectedAssocCompany && selectedAssocCompany.locations) {
        setLocations(selectedAssocCompany.locations);
      } else {
        setLocations([]);
      }
      // Reset location
      setFormData((prev: any) => ({
        ...prev,
        location_id: "",
      }));
    } else {
      setLocations([]);
    }
  }, [formData.associate_company_id, associateCompanies]);

  useEffect(() => {
    if (formData.location_id && locations.length > 0) {
      const selectedLocation = locations.find(
        (loc: any) => loc.id === formData.location_id
      );

      if (selectedLocation) {
        setFormData((prev: any) => ({
          ...prev,
          state_id: selectedLocation.state.id || "",
          city_id: selectedLocation.city.id || "",
        }));
      }
    } else {
      setFormData((prev: any) => ({
        ...prev,
        state_id: "",
        city_id: "",
      }));
    }
  }, [formData.location_id, locations]);

  const { licenseTypes: allLicenseTypes } = useGetLicenseTypes(
    {
      scopes: {
        // Use state-aware scope when state is available
        available_for_location: formData.associate_company_id && formData.state_id
        ? [formData.associate_company_id, formData.state_id]
          : null,
      },
      include: ['application_types'],
      includes: ['application_types'],
    },
    Boolean(formData.associate_company_id && formData.state_id)
  );
  const licenseTypes = useMemo(() => {
    if (!allLicenseTypes || allLicenseTypes.length === 0) return [];
    const filtered = allLicenseTypes.filter((licenseType: any) => {
      const appTypes = licenseType.application_types || [];
      console.log(`License Type: ${licenseType.name}, Application Types:`, appTypes);

      const hasNewType = appTypes.some((appType: any) =>
        appType.action_type === 'new' || appType.action_type === 'New'
      );
      const matchesCity = !licenseType.city_id ||
                          !formData.city_id ||
                          licenseType.city_id === formData.city_id;
      return hasNewType && matchesCity;
    });
    return filtered;
  }, [allLicenseTypes, formData.city_id]);
  const { states } = useGetStates({}, true);
  const { cities } = useGetCities(
    { filters: { state_id_eq: formData.state_id } },
    Boolean(formData.state_id)
  );
  const { applicationTypes, isFetching: isFetchingApplicationTypes } = useGetApplicationTypes(
    { filters: { license_type_id_eq: formData.license_type_id } },
    Boolean(formData.license_type_id)
  );
  const { records: licenseOwners } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        company_location_mappings_location_id_in: [formData.location_id],
        roles_name_in: ['License Owner'] 
      }
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );
  const { records: licenseWatchers } = useGetUsers(
    {
      filters: {
        companies_id_in: [formData.associate_company_id],
        company_location_mappings_location_id_in: [formData.location_id],
        roles_name_in: ['License Watcher']  
      }
    },
    Boolean(formData.associate_company_id && formData.location_id)
  );

  const { applications: existingApplications, isFetching: checkingDuplicateApplications } = useGetLicenseApplications(
    {
      includes: {
        cost_details: ['invoices']
      },
      filters: {
        associate_company_id_eq: formData.associate_company_id,
        location_id_eq: formData.location_id,
        license_type_id_eq: formData.license_type_id,
        status_in: ["not_started", "in_progress", "submitted", "under_review"],
      },
    },
    Boolean(formData.associate_company_id && formData.location_id && formData.license_type_id)
  );

  const { licenses: existingLicenses, isFetching: checkingDuplicateLicenses } = useGetCompanyLicenses(
    {
      filters: {
        associate_company_id_eq: formData.associate_company_id,
        location_id_eq: formData.location_id,
        license_type_id_eq: formData.license_type_id,
        status_in: ["active"],
      },
    },
    Boolean(formData.associate_company_id && formData.location_id && formData.license_type_id)
  );

  const hasDuplicateApplication = existingApplications && existingApplications.length > 0;
  const hasDuplicateLicense = existingLicenses && existingLicenses.length > 0;
  const hasDuplicate = hasDuplicateApplication || hasDuplicateLicense;
  const checkingDuplicate = checkingDuplicateApplications || checkingDuplicateLicenses;

  // Auto-select "New" application type when license_type is selected
   useEffect(() => {
    // Wait for applicationTypes to finish loading before attempting auto-selection
    if (isFetchingApplicationTypes) {
      console.log('Waiting for applicationTypes to load...');
      return;
    }

    if (formData.license_type_id && applicationTypes && applicationTypes.length > 0) {
      const newAppType = applicationTypes.find(
        (at: any) => at.action_type === 'new' || at.action_type === 'New'
      );

      if (newAppType && !formData.application_type_id) {
        console.log('Auto-selecting "New" application type:', newAppType.id);
        setFormData((prev: any) => ({
          ...prev,
          application_type_id: newAppType.id,
        }));
      } else if (!newAppType) {
        console.warn('No "New" application type found for license_type_id:', formData.license_type_id);
      }
    }
  }, [formData.license_type_id, applicationTypes, isFetchingApplicationTypes]);

  function getLastHyphenValue(str: string | null | undefined): string {
    if (!str) return "";
    const parts = str.split("-");
    return parts[parts.length - 1].trim();
  }

  // File upload
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Create mutation
  const { createApplication, creating } = useCreateLicenseApplication(
    (response: any) => {
      (response &&
      toast.success("New license application created successfully", {
        icon: <Icon name="snackbarSuccess" />,
      }))
      resetForm();
      closePanel();
      onSuccess && onSuccess();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errors = errorData && errorData.errors;

      if (errors && Array.isArray(errors) && errors.length > 0) {
        errors.forEach((errMsg: string) => {
          toast.error(errMsg, {
            icon: <Icon name="snackbarError" />,
          });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message, {
          icon: <Icon name="snackbarError" />,
        });
      } else {
        toast.error("Failed to create license application", {
          icon: <Icon name="snackbarError" />,
        });
      }
    }
  );

  // Auto-fill authority when license type is selected
  useEffect(() => {
    if (formData.license_type_id && licenseTypes) {
      const selectedType = licenseTypes.find(
        (lt: any) => lt.id === formData.license_type_id
      );
      if (selectedType) {
        setFormData((prev: any) => ({
          ...prev,
          authority: (selectedType && selectedType.authority) || "",
        }));
      }
    }
  }, [formData.license_type_id, licenseTypes]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Cost details handlers
  const handleOpenCostForm = (costTypeId: number) => {
    const costType = COST_TYPE_OPTIONS.find((ct) => ct.id === costTypeId);
    if (costType) {
      setCostFormPopup({
        isOpen: true,
        costTypeId: costType.id,
        costTypeLabel: costType.label,
        costTypeKey: costType.key,
      });
    }
  };

  const handleCloseCostForm = () => {
    if (costFormPopup.costTypeId && costFormPopup.costTypeKey) {
      const hasData = formData.cost_details[costFormPopup.costTypeKey];
      if (!hasData) {
        setFormData((prev: any) => ({
          ...prev,
          cost_type_ids: prev.cost_type_ids.filter((id: number) => id !== costFormPopup.costTypeId),
        }));
      }
    }
    setCostFormPopup({
      isOpen: false,
      costTypeId: null,
      costTypeLabel: "",
      costTypeKey: "",
    });
  };

  const handleSaveCostDetails = (costTypeKey: string, data: CostFormData) => {
    setFormData((prev: any) => ({
      ...prev,
      cost_details: {
        ...prev.cost_details,
        [costTypeKey]: data,
      },
    }));
    setCostFormPopup({
      isOpen: false,
      costTypeId: null,
      costTypeLabel: "",
      costTypeKey: "",
    });
    toast.success("Cost details saved", {
      icon: <Icon name="snackbarSuccess" />,
    });
  };

  const toggleCostAccordion = (costTypeKey: string) => {
    setExpandedCostAccordions((prev) =>
      prev.includes(costTypeKey) ? [] : [costTypeKey]
    );
  };

  const handleCostTypeChange = (costTypeId: number, checked: boolean) => {
    if (checked) {
      setFormData((prev: any) => ({
        ...prev,
        cost_type_ids: [...prev.cost_type_ids, costTypeId],
      }));
      handleOpenCostForm(costTypeId);
    } else {
      const costType = COST_TYPE_OPTIONS.find((ct) => ct.id === costTypeId);
      if (costType && formData.cost_details[costType.key]) {
        setCostTypeToDelete({
          id: costTypeId,
          label: costType.label,
          key: costType.key,
        });
        return;
      }
      setFormData((prev: any) => {
        const newCostDetails = { ...prev.cost_details };
        if (costType) {
          delete newCostDetails[costType.key];
        }
        return {
          ...prev,
          cost_type_ids: prev.cost_type_ids.filter((id: number) => id !== costTypeId),
          cost_details: newCostDetails,
        };
      });
    }
  };

  const handleConfirmDeleteCostType = () => {
    if (costTypeToDelete) {
      setFormData((prev: any) => {
        const newCostDetails = { ...prev.cost_details };
        delete newCostDetails[costTypeToDelete.key];
        return {
          ...prev,
          cost_type_ids: prev.cost_type_ids.filter((id: number) => id !== costTypeToDelete.id),
          cost_details: newCostDetails,
        };
      });
      setCostTypeToDelete(null);
    }
  };

  const validateStep1 = () => {
    if (!formData.company_id) {
      toast.error("Please select a company");
      return false;
    }
    if (!formData.associate_company_id) {
      toast.error("Please select an associate company");
      return false;
    }
    if (!formData.location_id) {
      toast.error("Please select a company location");
      return false;
    }
    if (!formData.state_id) {
      toast.error("Please select a state");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.license_name) {
      toast.error("Please enter license name");
      return false;
    }
    if (!formData.license_type_id) {
      toast.error("Please select a license type");
      return false;
    }
    if (!formData.authority) {
      toast.error("Authority is required");
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (formData.cost_type_ids.length > 0) {
      const missingDetails = formData.cost_type_ids.filter(
        (id: number) => {
          const costType = COST_TYPE_OPTIONS.find((ct) => ct.id === id);
          return costType && !formData.cost_details[costType.key];
        }
      );
      if (missingDetails.length > 0) {
        toast.error("Please fill cost details for all selected cost types");
        return false;
      }
    }
    return true;
  };

  const validateStep4 = () => {
    if (!formData.assigned_to_id) {
      toast.error("Please assign to a user");
      return false;
    }
    if (!formData.watcher_ids.length) {
      toast.error("Watcher is required.");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    if (currentStep === 3 && !validateStep3()) return;

    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const onSubmit = async () => {
    if (!validateStep4()) return;

    const costDetailsAttributes = Object.keys(formData.cost_details).map((costTypeKey) => {
      const costType = COST_TYPE_OPTIONS.find((ct) => ct.key === costTypeKey);
      const costData = formData.cost_details[costTypeKey];

      const invoicesAttributes = costData.uploaded_files
        ? costData.uploaded_files
            .filter((f: any) => f.uploaded_url)
            .map((f: any) => ({
              asset_type: "invoice",
              name: f.file_name || (f.file && f.file.name) || "document",
              s3_key: f.s3_key || "",
              s3_bucket: f.s3_bucket || "",
              s3_region: f.s3_region || "",
              file_path: f.uploaded_url,
              content_type: f.content_type || (f.file && f.file.type) || "",
              file_size: f.file_size || (f.file && f.file.size) || 0,
              status: "active",
              uploaded_by_id: user.user_id,
            }))
        : [];

      return {
        cost_category: costType ? costType.label : costTypeKey,
        cost_description: costData.cost_description,
        cost_amount: parseFloat(costData.cost_amount) || 0,
        tax_applicable: costData.tax_applicable,
        tax_amount: costData.tax_applicable ? (parseFloat(costData.tax_amount) || 0) : 0,
        total_cost: parseFloat(costData.total_cost_including_tax) || 0,
        vendor_name: costData.vendor_service_provider_name,
        payment_status: costData.payment_status,
        cost_period_from: costData.cost_period_from,
        cost_period_to: costData.cost_period_to,
        invoices_attributes: invoicesAttributes,
        created_by_id: user.user_id,
      };
    });

    const payload = {
    license_application: {
      company_id: formData.company_id,
      associate_company_id: formData.associate_company_id,
      location_id: formData.location_id,
      license_type_id: formData.license_type_id,
      state_id: formData.state_id,
      city_id: formData.city_id,
      license_name: formData.license_name,
      authority: formData.authority,
      application_type_id: formData.application_type_id,
      assigned_to_id: formData.assigned_to_id,
      requested_by_id: user.user_id,
      watcher_ids: formData.watcher_ids,
      status: formData.status,
      description: formData.description,
      created_by_id: user.user_id,
      updated_by_id: user.user_id,
      cost_details_attributes: costDetailsAttributes,
    },
  };

    createApplication(payload as any);
  };


  return (
    <>
      {isOpen && <div className="sidepanel-overlay"></div>}
      <div className="sidepanel-wrapper">
        <div className={`sidepanel ${isOpen ? "open" : ""}`}>
          <div className="sidepanel-header d-flex justify-content-between align-items-center pb-4">
            <h5 className="mb-0 fw-semibold">Create New Application</h5>
            <button
              className="btn-close"
              aria-label="Close"
              onClick={handleClosePanel}
            ></button>
          </div>

          <div className="sidepanel-body h-100">
            <ul className="progress-section p-0 m-0 pl-2">
              <li
                className={currentStep === 1 || currentStep > 1 ? "active" : ""}
              >
                <div className="d-flex align-items-center">
                  <div className="progress-circle d-flex justify-content-center align-items-center">
                    <div
                      className={`progress-checked ${
                        currentStep > 1 ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                  <div className="progress-line"></div>
                </div>
                <p>Step 1</p>
                <h6>Basic Details</h6>
              </li>
              <li
                className={currentStep === 2 || currentStep > 2 ? "active" : ""}
              >
                <div className="d-flex align-items-center">
                  <div className="progress-circle d-flex justify-content-center align-items-center">
                    <div
                      className={`progress-checked ${
                        currentStep > 2 ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                  <div className="progress-line"></div>
                </div>
                <p>Step 2</p>
                <h6>License Details</h6>
              </li>
              <li
                className={currentStep === 3 || currentStep > 3 ? "active" : ""}
              >
                <div className="d-flex align-items-center">
                  <div className="progress-circle d-flex justify-content-center align-items-center">
                    <div
                      className={`progress-checked ${
                        currentStep > 3 ? "completed" : ""
                      }`}
                    ></div>
                  </div>
                  <div className="progress-line"></div>
                </div>
                <p>Step 3</p>
                <h6>Cost Details</h6>
              </li>
              <li className={currentStep === 4 ? "active" : ""}>
                <div className="progress-circle d-flex justify-content-center align-items-center">
                  <div className="progress-checked"></div>
                </div>
                <p>Step 4</p>
                <h6>Assignment</h6>
              </li>
            </ul>

            <hr className="my-4" />

            {/* Step 1 - Basic Details */}
            {currentStep === 1 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">
                    Company<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.company_id}
                    onChange={(e) =>
                      handleInputChange("company_id", e.target.value)
                    }
                  >
                    <option value="">Select company</option>
                    {(companies || []).map((company: any) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Associate Company<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.associate_company_id}
                    onChange={(e) =>
                      handleInputChange("associate_company_id", e.target.value)
                    }
                    disabled={!formData.company_id}
                  >
                    <option value="">Select associate company</option>
                    {(associateCompanies || []).map((company: any) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Company Location<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.location_id}
                    onChange={(e) =>
                      handleInputChange("location_id", e.target.value)
                    }
                    disabled={!formData.associate_company_id}
                  >
                    <option value="">Select company location</option>
                    {(locations || []).map((location: any) => (
                      <option key={location.id} value={location.id}>
                        {location.name} - {getLastHyphenValue(location.company_location_address)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    State<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.state_id}
                    onChange={(e) =>
                      handleInputChange("state_id", e.target.value)
                    }
                    disabled
                  >
                    <option value="">Select state</option>
                    {(states || []).map((state: any) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    City
                  </label>
                  <select
                    className="form-select"
                    value={formData.city_id}
                    onChange={(e) =>
                      handleInputChange("city_id", e.target.value)
                    }
                    disabled
                  >
                    <option value="">Select city</option>
                    {(cities || []).map((city: any) => (
                      <option key={city.id} value={city.id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            )}

            {/* Step 2 - License Details */}
            {currentStep === 2 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">
                    License Name<span>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter license name"
                    value={formData.license_name}
                    onChange={(e) =>
                      handleInputChange("license_name", e.target.value)
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    License Type<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.license_type_id}
                    onChange={(e) =>
                      handleInputChange("license_type_id", e.target.value)
                    }
                  >
                    <option value="">Select license type</option>
                    {(licenseTypes || []).map((type: any) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <input
                  type="hidden"
                  name="application_type_id"
                  value={formData.application_type_id || ''}
                />


                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    id="description"
                    placeholder="Add description"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                  ></textarea>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Issuing Authority<span>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Authority"
                    value={formData.authority}
                    onChange={(e) =>
                      handleInputChange("authority", e.target.value)
                    }
                  />
                </div>

              </form>
            )}

            {/* Step 3 - Cost Details */}
            {currentStep === 3 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">
                    Select Cost Type
                  </label>
                  <div
                    className="watchers-box p-3 d-flex flex-wrap pb-0"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {COST_TYPE_OPTIONS.map((costType) => (
                      <div
                        key={costType.id}
                        className="mb-3 d-flex gap-2 align-items-center w-50"
                      >
                        <input
                          className="form-check"
                          style={{ minHeight: "15px" }}
                          type="checkbox"
                          id={`cost-type-${costType.id}`}
                          checked={formData.cost_type_ids.includes(costType.id)}
                          onChange={(e) => handleCostTypeChange(costType.id, e.target.checked)}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`cost-type-${costType.id}`}
                        >
                          <div className="text-md">{costType.label}</div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cost Details Accordions */}
                {COST_TYPE_OPTIONS.filter(
                  (costType) =>
                    formData.cost_type_ids.includes(costType.id) &&
                    formData.cost_details[costType.key]
                ).map((costType) => {
                  const costData = formData.cost_details[costType.key];
                  const isExpanded = expandedCostAccordions.includes(costType.key);
                  return (
                    <div key={costType.key} className="cost-accordion-item mb-3"
                     style={{ border: "1px solid #D1D5DB", borderRadius: "8px" }}>
                      <div
                        className="cost-accordion-header d-flex justify-content-between align-items-center"
                        style={{
                          background: "#F3F4F6",
                          cursor: "pointer",
                          transition: "border-radius 0.3s ease",
                          borderRadius: "8px",
                          padding: "10px 12px"
                        }}
                        onClick={() => toggleCostAccordion(costType.key)}
                      >
                        <span className="fw-semibold fs-6">{costType.label}</span>
                        <div className="d-flex align-items-center gap-3">
                          <button
                            type="button"
                            className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1 fw-semibold text-md"
                            style={{ color: "var(--royal-blue)" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenCostForm(costType.id);
                            }}
                          >
                            <Icon name="edit" />
                            Edit
                          </button>
                          <span style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}>
                            <Icon name="chevrondown" />
                          </span>
                        </div>
                      </div>
                      <div
                        className="cost-accordion-body"
                        style={{
                          maxHeight: isExpanded ? "2000px" : "0",
                          overflow: "hidden",
                          transition: "max-height 0.3s ease-in-out",
                          borderTop: "none",
                          borderRadius: "0 0 8px 8px",
                        }}
                      >
                        <div className="p-3 text-md">
                          <div className="row mb-3">
                            <div className="w-50 fw-semibold">Cost Description</div>
                            <div className="w-50 fw-medium">{costData.cost_description || "-"}</div>
                          </div>
                          <div className="row mb-3">
                            <div className="w-50 fw-semibold">Cost Amount</div>
                            <div className="w-50 fw-medium">{costData.cost_amount || "-"}</div>
                          </div>
                          <div className="row mb-3">
                            <div className="w-50 fw-semibold">Tax Applicable</div>
                            <div className="w-50 fw-medium">{costData.tax_applicable ? "Yes" : "No"}</div>
                          </div>
                          {costData.tax_applicable && (
                            <div className="row mb-3">
                              <div className="w-50 fw-semibold">Tax Amount</div>
                              <div className="w-50 fw-medium">{costData.tax_amount || "-"}</div>
                            </div>
                          )}
                          <div className="row mb-3">
                            <div className="w-50 fw-semibold">Total Cost {costData.tax_applicable ? "(Including Tax)" : ""}</div>
                            <div className="w-50 fw-medium">{costData.total_cost_including_tax || "-"}</div>
                          </div>
                          <div className="row mb-3">
                            <div className="w-50 fw-semibold">Vendor/Service Provider Name</div>
                            <div className="w-50 fw-medium">{costData.vendor_service_provider_name || "-"}</div>
                          </div>
                          <div className="row mb-3">
                            <div className="w-50 fw-semibold">Payment Status</div>
                            <div className="w-50 fw-medium">{costData.payment_status || "-"}</div>
                          </div>
                          <div className="row mb-3">
                            <div className="w-50 fw-semibold">Cost Period</div>
                            <div className="w-50 fw-medium">
                              {costData.cost_period_from && costData.cost_period_to
                                ? `${costData.cost_period_from} to ${costData.cost_period_to}`
                                : "-"}
                            </div>
                          </div>
                          {costData.uploaded_files && costData.uploaded_files.length > 0 && (
                            <div className="row mb-3">
                              <div className="w-50 fw-semibold">Cost Document</div>
                              <div className="w-50 fw-medium">
                                {costData.uploaded_files.map((fileData: any, idx: number) => (
                                  <div
                                    key={idx}
                                    className="d-flex align-items-center justify-content-between gap-2 mb-1 p-2"
                                    style={{
                                      background: "rgba(60, 147, 216, 0.10)",
                                      borderRadius: "6px",
                                      border: "1px solid #E5E7EB",
                                    }}
                                  >
                                    <span className="text-sm" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileData.file_name || (fileData.file && fileData.file.name) || "Document"}</span>
                                    <span
                                      style={{ color: "var(--royal-blue)", cursor: "pointer", minWidth: "24px" }}
                                      onClick={async () => {
                                        const fileName = fileData.file_name || (fileData.file && fileData.file.name) || "document";
                                        if (fileData.uploaded_url) {
                                          try {
                                            await downloadS3FileSecurely(fileName, fileData.uploaded_url);
                                          } catch (error) {
                                            toast.error("Failed to download file");
                                          }
                                        } else if (fileData.file) {
                                          const url = URL.createObjectURL(fileData.file);
                                          const link = document.createElement("a");
                                          link.href = url;
                                          link.download = fileData.file.name;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                          URL.revokeObjectURL(url);
                                        }
                                      }}
                                      title="Download"
                                    >
                                      <Icon name="download" />
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </form>
            )}

            {/* Step 4 - Assignment */}
            {currentStep === 4 && (
              <form>
                <div className="mb-3">
                  <label className="form-label">
                    Assigned to<span>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.assigned_to_id}
                    onChange={(e) =>
                      handleInputChange("assigned_to_id", e.target.value)
                    }
                  >
                    <option value="">Select an owner who will manage this license end to end</option>
                    {(licenseOwners || []).map((user: any) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Watchers<span>*</span></label>
                  <div
                    className="watchers-box p-3"
                    style={{ maxHeight: "200px", overflowY: "auto" }}
                  >
                    {(licenseWatchers || []).map((watcher: any) => (
                      <div
                        key={watcher.id}
                        className="mb-3 d-flex gap-2 align-items-start"
                      >
                        <input
                          className="form-check"
                          type="checkbox"
                          id={`watcher-${watcher.id}`}
                          checked={formData.watcher_ids.includes(watcher.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormData((prev: any) => ({
                              ...prev,
                              watcher_ids: checked
                                ? [...prev.watcher_ids, watcher.id]
                                : prev.watcher_ids.filter(
                                    (id: string) => id !== watcher.id
                                  ),
                            }));
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`watcher-${watcher.id}`}
                        >
                          <div className="fs-6">{watcher.name}</div>
                          <small className="text-sm">{watcher.email}</small>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>              
              </form>
            )}
          </div>
        </div>

        <div className={`button-container ${isOpen ? "open" : ""}`}>
          {currentStep === 1 && (
            <div className="w-100">
              <button
                type="button"
                className="button-primary w-100"
                onClick={handleNext}
              >
                Next
              </button>
            </div>
          )}
          {currentStep === 2 && (
            <>
              {hasDuplicate && (
                <div className="alert alert-danger mb-3" role="alert" style={{ fontSize: '14px' }}>
                  {hasDuplicateLicense ? (
                    <>
                      <strong>Active License Exists:</strong> An active license already exists with this license type for the selected company, associate company and location.
                    </>
                  ) : (
                    <>
                      <strong>Duplicate Application:</strong> A license application already exists with this license type for the selected company, associate company and location.
                    </>
                  )}
                </div>
              )}
              <div className="d-flex gap-3 w-100">
                <button
                  type="button"
                  className="button-secondary w-100"
                  onClick={handleBack}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="button-primary w-100"
                  onClick={handleNext}
                  disabled={hasDuplicate || checkingDuplicate}
                >
                  Next
                </button>
              </div>
            </>
          )}
          {currentStep === 3 && (
            <div className="d-flex gap-3 w-100">
              <button
                type="button"
                className="button-secondary w-100"
                onClick={handleBack}
              >
                Back
              </button>
              <button
                type="button"
                className="button-primary w-100"
                onClick={handleNext}
              >
                Next
              </button>
            </div>
          )}
          {currentStep === 4 && (
            <div className="d-flex gap-3 w-100">
              <button
                type="button"
                className="button-secondary w-100"
                onClick={handleBack}
              >
                Back
              </button>
              <button
                type="button"
                className="button-primary w-100"
                onClick={onSubmit}
                disabled={creating}
              >
                {creating ? "Saving..." : "Done"}
              </button>
            </div>
          )}
        </div>
      </div>
      {creating && <PageLoader />}

      {/* Cost Details Form Popup */}
      <CostDetailsForm
        isOpen={costFormPopup.isOpen}
        costTypeLabel={costFormPopup.costTypeLabel}
        costTypeKey={costFormPopup.costTypeKey}
        onClose={handleCloseCostForm}
        onSave={handleSaveCostDetails}
        initialData={
          costFormPopup.costTypeKey
            ? formData.cost_details[costFormPopup.costTypeKey]
            : undefined
        }
        resourceType="license_application"
      />

      {/* Cost Type Deletion Confirmation Popup */}
      {costTypeToDelete && (
        <div className="cost-details-form-wrapper">
          <div className="license-popup-overlay"></div>
          <div className="cost-details-popup">
            <div className="popup-header d-flex justify-content-between align-items-start">
              <h5 className="mb-0 fw-semibold">Are you sure you want to uncheck this cost type?</h5>
              <button
                className="btn-close"
                aria-label="Close"
                onClick={() => setCostTypeToDelete(null)}
              ></button>
            </div>

            <hr className="mb-3 mt-0" />

            <div className="popup-body">
              <p className="mb-4">
                Unchecking this option will remove the details added against this cost type.
              </p>
            </div>

            <hr className="mb-3 mt-0" />

            <div className="popup-footer d-flex gap-3">
              <button
                type="button"
                className="button-secondary px-4 w-50"
                onClick={() => setCostTypeToDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button-primary px-4 w-50"
                onClick={handleConfirmDeleteCostType}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewApplicationPanel;
